/**
 * SetOperatorKindDropdown — replaces SetOperatorBar's disabled kind badge
 * with a real shadcn DropdownMenu once the parent statement's edit context is
 * ready (schema snapshot loaded). Selecting a choice commits a
 * `modify-set-operator` StructuralEdit via `modifySetOperator`, replacing the
 * separator's whole recorded span (keyword + any ALL/DISTINCT modifier)
 * byte-precisely — every other byte of the compound statement is preserved.
 *
 * Per §2.3 S7 decision 2, `branchIndex` is the SEPARATOR index and the commit
 * MUST use the PARENT statement's edit context, not a per-branch derived one
 * (`deriveBranchEditContext`'s synthetic per-branch `statementInfo` cannot see
 * the separator between branches) — callers thread the same `editContext`
 * `SetOperatorBranches` receives from its own `editContext` prop.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — set-operator switch (§2.3 S7).
 */

import { ChevronsUpDown } from 'lucide-react';

import { cn } from '../../../../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import type { ParseOptions } from '../../../../sql-grammar/model';
import { extractStructureForContext, toCommitCtx, type VqeEditContext } from '../../edit/editContext';
import { modifySetOperator } from '../../edit/edits/modifySetOperator';
import { useVqeStore } from '../../store/vqeStore';
import type { SetOpKind } from './extractSetOperatorBranches';
import {
  KIND_BADGE_CLASS,
  setOperatorChoicesForEngine,
  type SetOperatorChoice,
} from './setOperatorKindMaps';

export interface SetOperatorKindDropdownProps {
  readonly currentKind: SetOpKind;
  readonly currentAll: boolean;
  readonly branchIndex: number;
  readonly editContext: VqeEditContext;
  readonly options: ParseOptions;
  readonly testIdPrefix: string;
}

export function SetOperatorKindDropdown({
  currentKind,
  currentAll,
  branchIndex,
  editContext,
  options,
  testIdPrefix,
}: SetOperatorKindDropdownProps) {
  const label = currentAll ? `${currentKind} ALL` : currentKind;
  const choices = setOperatorChoicesForEngine(options.engine);

  const onSelect = (choice: SetOperatorChoice): void => {
    if (choice.kind === currentKind && choice.all === currentAll) return;
    const structure = extractStructureForContext(editContext);
    modifySetOperator(branchIndex, choice.kind, choice.all, toCommitCtx(editContext), { structure }).catch(
      (err) => {
        useVqeStore.getState().recordCommitFeedback({
          kind: 'failure',
          at: Date.now(),
          message: err instanceof Error ? err.message : String(err),
        });
      },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type="button"
        aria-label={`${currentKind} operator dropdown`}
        data-testid={`${testIdPrefix}-kind-dropdown`}
        className={cn(
          'inline-flex items-center gap-1 rounded-md border px-2.5 py-0.5 text-[11px] font-mono font-semibold uppercase tracking-wide cursor-pointer hover:opacity-90',
          KIND_BADGE_CLASS[currentKind],
        )}
      >
        <span data-testid={`${testIdPrefix}-label`}>{label}</span>
        <ChevronsUpDown className="h-3 w-3 opacity-50" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" data-testid={`${testIdPrefix}-kind-menu`}>
        {choices.map((choice) => {
          const isCurrent = choice.kind === currentKind && choice.all === currentAll;
          return (
            <DropdownMenuItem
              key={choice.optionId}
              data-testid={`${testIdPrefix}-kind-option-${choice.optionId}`}
              onSelect={() => onSelect(choice)}
              className={isCurrent ? 'font-semibold' : undefined}
            >
              {choice.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
